import { useState, useEffect } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CheckCircle, XCircle, ArrowClockwise, LinkSimple, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACTION_COLOR = {
  approve: "text-emerald-400",
  reject: "text-rose-400",
  resubmit: "text-blue-400",
};

export default function SubmissionDrawer({ submissionId, open, onClose, role, onUpdated }) {
  const [sub, setSub] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !submissionId) return;
    (async () => {
      try {
        const { data } = await api.get(`/submissions/${submissionId}`);
        setSub(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, [open, submissionId]);

  const review = async (action) => {
    if (action === "reject" && !feedback.trim()) {
      toast.error("Please provide feedback for rejection");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/submissions/${submissionId}/review`, { action, feedback });
      toast.success(action === "approve" ? "Content approved" : "Feedback sent to influencer");
      setFeedback("");
      onUpdated && onUpdated();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="bg-black border-white/10 text-zinc-50 sm:max-w-xl overflow-y-auto" data-testid="submission-drawer">
        <SheetHeader>
          <SheetTitle className="font-heading font-black text-2xl text-zinc-50">Submission</SheetTitle>
        </SheetHeader>

        {sub ? (
          <div className="mt-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-mono text-zinc-500">CAMPAIGN</div>
                <div className="font-heading font-bold">{sub.campaign_name || sub.campaign_id}</div>
              </div>
              <StatusBadge status={sub.status} />
            </div>

            <div className="rounded-xl border border-white/10 bg-zinc-900/60 overflow-hidden">
              {sub.file_id && sub.media_type === "video" && (
                <video src={fileUrl(sub.file_id)} controls className="w-full max-h-96 bg-black" />
              )}
              {sub.file_id && sub.media_type === "image" && (
                <img src={fileUrl(sub.file_id)} alt="Content" className="w-full max-h-96 object-contain bg-black" />
              )}
              {sub.external_url && (
                <a
                  href={sub.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 p-4 text-yellow-400 hover:text-yellow-300 text-sm border-t border-white/5"
                  data-testid="submission-external-link"
                >
                  <LinkSimple size={16} /> {sub.external_url}
                </a>
              )}
              <div className="p-4">
                <div className="text-xs font-mono text-zinc-500 mb-1">CAPTION</div>
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{sub.caption}</p>
                <div className="text-xs text-zinc-500 mt-3">
                  By <span className="text-zinc-300">{sub.influencer_name}</span> · Revisions: {sub.revision_count}
                </div>
              </div>
            </div>

            {/* Feedback thread */}
            {sub.feedback_thread?.length > 0 && (
              <div>
                <div className="text-xs font-mono text-zinc-500 mb-2 flex items-center gap-2">
                  <ChatCircle size={14} /> FEEDBACK HISTORY
                </div>
                <div className="space-y-3">
                  {sub.feedback_thread.map((f) => (
                    <div key={`${f.created_at}-${f.author_id}-${f.action}`} className="rounded-lg border border-white/10 bg-zinc-900/60 p-3">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium text-zinc-200">
                          {f.author_name} <span className="text-zinc-500 font-mono">({f.author_role})</span>
                        </span>
                        <span className={`font-mono uppercase ${ACTION_COLOR[f.action] || "text-zinc-400"}`}>{f.action}</span>
                      </div>
                      {f.message && <p className="text-sm text-zinc-300 mt-1">{f.message}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Review actions for brand/agency */}
            {(role === "brand" || role === "agency_admin") && sub.status === "pending" && (
              <div className="border-t border-white/10 pt-5 space-y-3">
                <div>
                  <div className="text-xs font-mono text-zinc-500 mb-1.5">FEEDBACK (required for rejection)</div>
                  <Textarea
                    data-testid="review-feedback-input"
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    placeholder="What changes are needed?"
                    className="bg-zinc-900 border-white/10 text-zinc-100 min-h-24"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    data-testid="review-reject-button"
                    onClick={() => review("reject")}
                    disabled={busy}
                    variant="outline"
                    className="flex-1 border-rose-500/20 bg-rose-500/5 hover:bg-rose-500/10 text-rose-300"
                  >
                    <XCircle size={16} weight="fill" className="mr-2" /> Reject
                  </Button>
                  <Button
                    data-testid="review-approve-button"
                    onClick={() => review("approve")}
                    disabled={busy}
                    className="flex-1 bg-yellow-400 hover:bg-yellow-300 text-black font-bold"
                  >
                    <CheckCircle size={16} weight="fill" className="mr-2" /> Approve
                  </Button>
                </div>
              </div>
            )}

            {sub.status === "approved" && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4 text-emerald-300 text-sm flex items-center gap-2">
                <CheckCircle weight="fill" /> Approved — influencer is cleared to publish.
              </div>
            )}
            {sub.status === "rejected" && role === "influencer" && (
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 text-blue-300 text-sm flex items-center gap-2">
                <ArrowClockwise weight="bold" /> Revise and resubmit from your dashboard.
              </div>
            )}
          </div>
        ) : (
          <div className="text-zinc-500 text-sm mt-6">Loading…</div>
        )}
      </SheetContent>
    </Sheet>
  );
}
